import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators, AbstractControl } from '@angular/forms';
import { SuppliersService } from '../../../../services/suppliers.service';
import { StatusService } from '../../../../services/status.service';
import { ViaCepService, ViaCepResponse } from '../../../../services/viacep.service';
import { SupplierAttachment } from '../../../../models/Supplier';
import { Status } from '../../../../models/Status';

@Component({
  selector: 'app-suppliers-form',
  templateUrl: './suppliers-form.component.html',
  styleUrls: ['./suppliers-form.component.css']
})
export class SuppliersFormComponent implements OnInit {
  formStep1: FormGroup;
  formStep2: FormGroup;
  formStep3: FormGroup;
  availableTabs = [
    { id: 'identification', label: 'Identificação', icon: 'fas fa-building', required: true, completed: false },
    { id: 'contact', label: 'Contato', icon: 'fas fa-address-book', required: true, completed: false },
    { id: 'status', label: 'Status e Observações', icon: 'fas fa-cog', required: false, completed: false }
  ];
  activeTab = 'identification';
  supplierId?: number;
  originalCnpjDigits?: string;
  attachments: SupplierAttachment[] = [];
  uploading = false;
  uploadProgress = 0;
  isSubmitting = false;
  // Status
  loadingStatuses = false;
  statusOptions: Status[] = [];
  // CEP
  isLoadingCep = false;
  cepError: string | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private suppliersService: SuppliersService,
    private statusService: StatusService,
    private viaCepService: ViaCepService
  ) {
    this.formStep1 = this.fb.group({
      companyName: ['', Validators.required],
      tradeName: [''],
      cnpj: ['', [Validators.required, Validators.minLength(14)]],
      stateRegistration: ['']
    });
    this.formStep2 = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      primaryPhone: ['', [Validators.required, Validators.minLength(10)]],
      secondaryPhone: [''],
      supplierCategory: [''],
      street: [''],
      number: [''],
      complement: [''],
      neighborhood: [''],
      city: [''],
      state: [''],
      zipCode: ['']
    });
    this.formStep3 = this.fb.group({
      notes: [''],
      status: [''],
      blocked: [false]
    });
  }

  ngOnInit(): void {
    this.loadSupplierStatuses();
    this.formStep1.valueChanges.subscribe(() => this.updateTabsCompletionStatus());
    this.formStep2.valueChanges.subscribe(() => this.updateTabsCompletionStatus());
    this.formStep3.valueChanges.subscribe(() => this.updateTabsCompletionStatus());
    const idParam = this.route.snapshot.paramMap.get('id');
    if (idParam) {
      this.supplierId = Number(idParam);
      this.suppliersService.getById(this.supplierId).subscribe(s => {
        this.formStep1.patchValue({ 
          companyName: s.companyName, 
          tradeName: s.tradeName, 
          cnpj: s.cnpj,
          stateRegistration: s.stateRegistration 
        });
        this.originalCnpjDigits = this.onlyDigits(s.cnpj);
        this.formStep2.patchValue({ 
          email: s.email, 
          primaryPhone: s.primaryPhone, 
          secondaryPhone: s.secondaryPhone,
          supplierCategory: s.supplierCategory,
          street: s.street,
          number: s.number,
          complement: s.complement,
          neighborhood: s.neighborhood,
          city: s.city,
          state: s.state,
          zipCode: s.zipCode
        });
        this.formStep3.patchValue({ 
          notes: s.notes, 
          status: s.active ? 'Ativo' : 'Inativo', 
          blocked: s.blocked 
        });
      });
      this.loadAttachments();
    } else {
      this.applyDefaultStatusForNewSupplier();
    }
    this.updateTabsCompletionStatus();
  }

  selectTab(tabId: string): void {
    if (tabId === this.activeTab) return;
    const targetTab = this.availableTabs.find(tab => tab.id === tabId);
    if (targetTab) {
      this.activeTab = tabId;
    }
  }

  getCurrentTabIndex(): number {
    return this.availableTabs.findIndex(tab => tab.id === this.activeTab);
  }

  isFirstTab(): boolean {
    return this.getCurrentTabIndex() === 0;
  }

  isLastTab(): boolean {
    return this.getCurrentTabIndex() === this.availableTabs.length - 1;
  }

  goToNextTab(): void {
    const currentIndex = this.getCurrentTabIndex();
    if (currentIndex < this.availableTabs.length - 1) {
      this.activeTab = this.availableTabs[currentIndex + 1].id;
    }
  }

  goToPreviousTab(): void {
    const currentIndex = this.getCurrentTabIndex();
    if (currentIndex > 0) {
      this.activeTab = this.availableTabs[currentIndex - 1].id;
    }
  }

  save(): void {
    if (!this.isTabValid('status')) {
      this.formStep3.markAllAsTouched();
      return;
    }

    // Validação final de segurança
    if (!this.isTabValid('identification') || !this.isTabValid('contact')) {
      return;
    }

    this.isSubmitting = true;
    const formData = { ...this.formStep1.value, ...this.formStep2.value, ...this.formStep3.value };
    // Converter o status selecionado para o campo active (backward compatibility)
    const payload = {
      ...formData,
      active: formData.status === 'Ativo' || formData.status === ''
    };
    if (this.supplierId) {
      this.suppliersService.update(this.supplierId, payload).subscribe({
        next: () => {
          this.isSubmitting = false;
          this.router.navigate(['/purchasing/suppliers']);
        },
        error: () => {
          this.isSubmitting = false;
        }
      });
    } else {
      this.suppliersService.create(payload).subscribe({
        next: () => {
          this.isSubmitting = false;
          this.router.navigate(['/purchasing/suppliers']);
        },
        error: () => {
          this.isSubmitting = false;
        }
      });
    }
  }

  cancel(): void { this.router.navigate(['/purchasing/suppliers']); }

  loadSupplierStatuses(): void {
    this.loadingStatuses = true;
    this.statusService.getByEntity('Supplier', true)
      .subscribe({
        next: (statuses: Status[]) => {
          this.statusOptions = statuses;
          this.applyDefaultStatusForNewSupplier();
          this.loadingStatuses = false;
        },
        error: (err) => {
          console.error('Erro ao carregar status de fornecedores:', err);
          this.loadingStatuses = false;
          // Fallback para status básicos em caso de erro
          this.statusOptions = [
            { id: 1, name: 'Ativo', description: 'Fornecedor ativo', entityType: 'Supplier', isActive: true },
            { id: 2, name: 'Inativo', description: 'Fornecedor inativo', entityType: 'Supplier', isActive: true }
          ] as Status[];
          this.applyDefaultStatusForNewSupplier();
        }
      });
  }

  private applyDefaultStatusForNewSupplier(): void {
    // Regra solicitada: em "Novo Fornecedor", status deve iniciar como "Ativo".
    if (this.supplierId) return;

    const currentStatus = this.formStep3.get('status')?.value;
    if (currentStatus) return;

    this.formStep3.patchValue({ status: this.getDefaultStatusName() });
  }

  private getDefaultStatusName(): string {
    const activeByName = this.statusOptions.find(
      status => (status.name || '').trim().toLowerCase() === 'ativo'
    );
    const activeByFlag = this.statusOptions.find(status => status.isActive);
    return activeByName?.name || activeByFlag?.name || 'Ativo';
  }

  isFieldInvalid(step: number, fieldName: string): boolean {
    let form: FormGroup;
    switch (step) {
      case 1:
        form = this.formStep1;
        break;
      case 2:
        form = this.formStep2;
        break;
      case 3:
        form = this.formStep3;
        break;
      default:
        return false;
    }
    const field = form.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  isTabValid(tabId: string): boolean {
    switch (tabId) {
      case 'identification':
        return this.formStep1.valid;
      case 'contact':
        return this.formStep2.valid && !this.cepError;
      case 'status':
        return this.formStep3.valid;
      default:
        return true;
    }
  }

  isTabComplete(tabId: string): boolean {
    if (tabId !== 'status') {
      return this.isTabValid(tabId);
    }

    // No cadastro novo, o status padrão não deve marcar a aba como concluída (verde) automaticamente.
    if (!this.supplierId) {
      const statusValue = String(this.formStep3.get('status')?.value || '').trim().toLowerCase();
      const notesValue = String(this.formStep3.get('notes')?.value || '').trim();
      const blockedValue = !!this.formStep3.get('blocked')?.value;
      const defaultStatus = this.getDefaultStatusName().trim().toLowerCase();

      const isOnlyDefaultState = !notesValue && !blockedValue && statusValue === defaultStatus;
      if (isOnlyDefaultState) {
        return false;
      }
    }

    return this.isTabValid(tabId);
  }

  updateTabsCompletionStatus(): void {
    this.availableTabs.forEach(tab => {
      tab.completed = this.isTabComplete(tab.id);
    });
  }

  // ===== CEP (ViaCEP) =====
  onZipCodeInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const masked = this.maskCEP(input.value || '');
    this.formStep2.get('zipCode')?.setValue(masked, { emitEvent: false });

    const digits = this.onlyDigits(masked);
    if (digits.length === 8) {
      this.fetchAddressByCEP(digits);
    } else {
      this.cepError = null;
    }
  }

  // ===== Máscaras de Entrada =====
  onCnpjInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const masked = this.maskCNPJ(input.value || '');
    this.formStep1.get('cnpj')?.setValue(masked, { emitEvent: false });
  }

  onCnpjBlur(): void {
    const control = this.formStep1.get('cnpj');
    const rawValue = (control?.value || '') as string;
    const digits = this.onlyDigits(rawValue);
    if (!digits || digits.length !== 14) {
      this.clearCnpjExistsError();
      return;
    }
    if (this.supplierId && this.originalCnpjDigits && digits === this.originalCnpjDigits) {
      this.clearCnpjExistsError();
      return;
    }
    control?.markAsTouched();
    this.suppliersService.getByDocument(digits).subscribe({
      next: supplier => {
        const exists =
          !!supplier &&
          supplier.id != null &&
          Number(supplier.id) > 0 &&
          (!this.supplierId || supplier.id !== this.supplierId);
        if (exists) {
          this.setCnpjExistsError(control);
        } else {
          this.clearCnpjExistsError();
        }
      },
      error: () => {
        // Falha de rede / 5xx: não marcar como CNPJ duplicado (backend pode validar no salvar).
        this.clearCnpjExistsError();
      }
    });
  }

  onPhoneInput(event: Event, controlName: string): void {
    const input = event.target as HTMLInputElement;
    const masked = this.maskPhone(input.value || '');
    this.formStep2.get(controlName)?.setValue(masked, { emitEvent: false });
  }

  private onlyDigits(value: string | undefined): string {
    return (value || '').replace(/\D+/g, '');
  }

  private maskCEP(value: string): string {
    let v = this.onlyDigits(value).slice(0, 8);
    if (v.length > 5) {
      v = v.replace(/^(\d{5})(\d)/, '$1-$2');
    }
    return v;
  }

  private maskCNPJ(value: string): string {
    let v = this.onlyDigits(value).slice(0, 14);
    if (v.length > 12) {
      v = v.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    } else if (v.length > 8) {
      v = v.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})/, '$1.$2.$3/$4');
    } else if (v.length > 5) {
      v = v.replace(/^(\d{2})(\d{3})(\d{3})/, '$1.$2.$3');
    } else if (v.length > 2) {
      v = v.replace(/^(\d{2})(\d{3})/, '$1.$2');
    }
    return v;
  }

  private maskPhone(value: string): string {
    let v = this.onlyDigits(value).slice(0, 11);
    if (v.length > 10) {
      v = v.replace(/^(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    } else if (v.length > 6) {
      v = v.replace(/^(\d{2})(\d{4})(\d{0,4})/, '($1) $2-$3');
    } else if (v.length > 2) {
      v = v.replace(/^(\d{2})(\d{0,5})/, '($1) $2');
    } else {
      v = v.replace(/^(\d{0,2})/, '($1');
    }
    return v;
  }

  private setCnpjExistsError(control: AbstractControl | null): void {
    if (!control) return;
    const currentErrors = control.errors || {};
    if (currentErrors['cnpjExists']) return;
    control.setErrors({ ...currentErrors, cnpjExists: true });
  }

  private clearCnpjExistsError(): void {
    const control = this.formStep1.get('cnpj');
    if (!control) return;
    const currentErrors = control.errors;
    if (!currentErrors || !currentErrors['cnpjExists']) return;
    const { cnpjExists, ...rest } = currentErrors;
    control.setErrors(Object.keys(rest).length ? rest : null);
  }

  private setupStatusMutualExclusion(): void {
    const activeControl = this.formStep3.get('active');
    const blockedControl = this.formStep3.get('blocked');

    activeControl?.valueChanges.subscribe(value => {
      if (value && blockedControl?.value) {
        blockedControl.setValue(false, { emitEvent: false });
      }
    });

    blockedControl?.valueChanges.subscribe(value => {
      if (value && activeControl?.value) {
        activeControl.setValue(false, { emitEvent: false });
      }
    });
  }

  private fetchAddressByCEP(cepDigits: string): void {
    if (!cepDigits || cepDigits.length !== 8) return;
    this.isLoadingCep = true;
    this.cepError = null;
    this.viaCepService.lookup(cepDigits).subscribe({
      next: (res: ViaCepResponse) => {
        this.isLoadingCep = false;
        if ((res as any)?.erro) {
          this.cepError = 'CEP não encontrado.';
          return;
        }
        this.formStep2.patchValue({
          zipCode: res.cep || this.formStep2.get('zipCode')?.value,
          street: res.logradouro || this.formStep2.get('street')?.value,
          neighborhood: res.bairro || this.formStep2.get('neighborhood')?.value,
          city: res.localidade || this.formStep2.get('city')?.value,
          state: res.uf || this.formStep2.get('state')?.value,
          complement: res.complemento || this.formStep2.get('complement')?.value
        }, { emitEvent: false });
      },
      error: () => {
        this.isLoadingCep = false;
        this.cepError = 'Erro ao consultar o CEP.';
      }
    });
  }

  loadAttachments(): void {
    if (!this.supplierId) return;
    this.suppliersService.listAttachments(this.supplierId).subscribe(a => this.attachments = a);
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;
    const file = input.files[0];
    
    // Para novos fornecedores, apenas simular o upload
    if (!this.supplierId) {
      this.uploading = true;
      this.uploadProgress = 0;
      
      // Simular progresso de upload
      const progressInterval = setInterval(() => {
        if (this.uploadProgress < 90) {
          this.uploadProgress += 10;
        }
      }, 200);

      // Simular upload bem-sucedido após 2 segundos
      setTimeout(() => {
        this.uploading = false;
        this.uploadProgress = 100;
        clearInterval(progressInterval);
        
        // Criar um anexo simulado
        const mockAttachment: SupplierAttachment = {
          id: Date.now(),
          supplierId: 0,
          fileName: file.name,
          originalFileName: file.name,
          filePath: '#',
          contentType: file.type || 'application/octet-stream',
          fileSize: file.size,
          category: 'Temporário',
          description: 'Anexo temporário - será salvo após criar o fornecedor',
          uploadDate: new Date().toISOString(),
          uploadedBy: 'Usuário',
          active: true
        };
        
        this.attachments = [mockAttachment, ...this.attachments];
      }, 2000);
      
      return;
    }
    
    // Para fornecedores existentes, fazer upload real
    this.uploading = true;
    this.uploadProgress = 0;
    
    // Simular progresso de upload
    const progressInterval = setInterval(() => {
      if (this.uploadProgress < 90) {
        this.uploadProgress += 10;
      }
    }, 200);

    this.suppliersService.uploadAttachment(this.supplierId, file).subscribe({
      next: att => { 
        this.attachments = [att, ...this.attachments]; 
        this.uploading = false; 
        this.uploadProgress = 100;
        clearInterval(progressInterval);
      },
      error: () => { 
        this.uploading = false; 
        this.uploadProgress = 0;
        clearInterval(progressInterval);
      }
    });
  }

  removeAttachment(attachment: SupplierAttachment): void {
    // Para anexos temporários (novos fornecedores), apenas remover da lista
    if (!this.supplierId || attachment.supplierId === 0) {
      this.attachments = this.attachments.filter(a => a.id !== attachment.id);
      return;
    }
    
    // Para fornecedores existentes, fazer remoção real
    this.suppliersService.removeAttachment(this.supplierId, attachment.id).subscribe(() => {
      this.attachments = this.attachments.filter(a => a.id !== attachment.id);
    });
  }


}


